
import React, { useState, useMemo } from 'react';
import { DownloadIcon } from './icons';
import { useApp } from '../context/AppContext'; // Import the useApp hook
import { LOCAL_STORAGE_KEY } from '../constants';

const THEMES = [
    { name: 'sky', color: 'bg-sky-500' },
    { name: 'emerald', color: 'bg-emerald-500' },
    { name: 'rose', color: 'bg-rose-500' },
    { name: 'violet', color: 'bg-violet-500' },
    { name: 'amber', color: 'bg-amber-500' },
    { name: 'light', color: 'bg-slate-200', border: 'border-slate-400' },
];

const TOKEN_ICONS = ['🌟', '💎', '🪙', '✨', '🏆', '❤️', '🔥', '✅'];

const TabButton: React.FC<{ name: string, activeTab: string, onClick: (name:string) => void, children: React.ReactNode }> = ({ name, activeTab, onClick, children }) => {
    const isActive = activeTab === name;
    return (
        <button
            onClick={() => onClick(name)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                isActive 
                ? 'border-[var(--primary-500)] text-[var(--primary-500)]' 
                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
        >
            {children}
        </button>
    );
}

const SettingsModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { settings, quests, daily, version } = useApp(); // Consume the context
  const importRef = React.useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState('appearance');
  const [isConfirmingReset, setIsConfirmingReset] = useState(false);

  // --- Handlers ---
  const handleResetClick = () => setIsConfirmingReset(true);
  const cancelReset = () => setIsConfirmingReset(false);
  const confirmReset = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    localStorage.removeItem('backgroundImage');
    window.location.reload();
  };

  const onExport = () => {
    const fullState = {
        version,
        date: new Date().toISOString().slice(0,10),
        ...settings,
        ...quests,
        ...daily,
    };
    const dataStr = JSON.stringify(fullState, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `power-up-pact_${new Date().toISOString().slice(0, 10)}.json`;
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const onImport = (_: React.ChangeEvent<HTMLInputElement>) => {
    alert("Import functionality is being refactored.");
  };

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const result = e.target?.result;
                if (typeof result === 'string') {
                    localStorage.setItem('backgroundImage', result);
                    settings.setBackgroundImage(result);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleClearImage = () => {
        localStorage.removeItem('backgroundImage');
        settings.setBackgroundImage(null);
    };

  // --- Memos ---
  const questlines = useMemo(() => {
    const lines: { [key: string]: { name: string; id: string } } = {};
    quests.quests.forEach(q => {
        if (q.questlineId && q.isQuestlineStart) {
            lines[q.questlineId] = { id: q.questlineId, name: q.title };
        }
    });
    return Object.values(lines).sort((a,b) => a.name.localeCompare(b.name));
  }, [quests.quests]);

  const stats = useMemo(() => {
    const byYear: { [year: string]: { tokens: number, quests: number }} = {};
    let lifetimeTokens = 0;
    let lifetimeQuests = 0;

    if (!daily.history) return { byYear: {}, sortedYears: [], lifetime: { tokens: 0, quests: 0 } };

    Object.keys(daily.history).forEach(dateStr => {
        const year = dateStr.substring(0, 4);
        const day = daily.history[dateStr];
        
        if (!byYear[year]) {
            byYear[year] = { tokens: 0, quests: 0 };
        }

        byYear[year].tokens += day.tokens || 0;
        byYear[year].quests += day.questsCompleted?.length || 0;
        
        lifetimeTokens += day.tokens || 0;
        lifetimeQuests += day.questsCompleted?.length || 0;
    });

    const sortedYears = Object.keys(byYear).sort((a,b) => b.localeCompare(a));

    return {
        byYear,
        sortedYears,
        lifetime: { tokens: lifetimeTokens, quests: lifetimeQuests }
    };
  }, [daily.history]);

  const SizingButton: React.FC<{ label: string, sizing: 'cover' | 'repeat' }> = ({ label, sizing }) => {
    const isActive = settings.backgroundImageSizing === sizing;
    return (
      <button
        onClick={() => settings.setBackgroundImageSizing(sizing)}
        className={`flex-1 px-4 py-2 font-semibold rounded-lg transition-colors text-sm ${
          isActive ? 'text-white' : 'bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-500 text-slate-800 dark:text-white'
        }`}
        style={isActive ? { backgroundColor: 'var(--primary-600)' } : {}}
      >
        {label}
      </button>
    );
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
    >
      <div 
        className="relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-0 w-full max-w-md border border-slate-300 dark:border-slate-700 flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {isConfirmingReset && (
          <div className="absolute inset-0 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm flex flex-col items-center justify-center rounded-xl z-20 p-4">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">Are you sure?</h3>
              <p className="text-center text-slate-600 dark:text-slate-300 mt-2 mb-6">This will permanently delete all your data, including quests and tokens. This action cannot be undone.</p>
              <div className="flex gap-4 w-full">
                  <button onClick={cancelReset} className="flex-1 px-4 py-2 bg-slate-200 dark:bg-slate-600 text-slate-800 dark:text-white font-semibold rounded-lg hover:bg-slate-300 dark:hover:bg-slate-500 transition-colors">
                      Cancel
                  </button>
                  <button onClick={confirmReset} className="flex-1 px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors">
                      Confirm Reset
                  </button>
              </div>
          </div>
        )}

        <div className="flex justify-between items-center p-6 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Settings</h2>
            <button onClick={onClose} className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-2xl leading-none">&times;</button>
        </div>
        
        <div className="flex border-b border-slate-200 dark:border-slate-700 px-3">
            <TabButton name="appearance" activeTab={activeTab} onClick={setActiveTab}>Appearance</TabButton>
            <TabButton name="quests" activeTab={activeTab} onClick={setActiveTab}>Quests</TabButton>
            <TabButton name="stats" activeTab={activeTab} onClick={setActiveTab}>Stats</TabButton>
            <TabButton name="data" activeTab={activeTab} onClick={setActiveTab}>Data & Reset</TabButton>
        </div>

        <div className="p-6 space-y-8 overflow-y-auto max-h-[70vh]">
            {activeTab === 'appearance' && (
                <div className="space-y-6">
                    <div>
                        <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3">Theme Color</h3>
                        <div className="flex items-center gap-4">
                            {THEMES.map(theme => (
                                <button 
                                    key={theme.name}
                                    onClick={() => settings.setTheme(theme.name)}
                                    className={`w-10 h-10 rounded-full ${theme.color} ${theme.border || ''} transition-transform duration-200 ${settings.theme === theme.name ? 'ring-2 ring-offset-2 ring-offset-white dark:ring-offset-slate-800 ring-[var(--primary-500)]' : 'hover:scale-110'}`}
                                    aria-label={`Set ${theme.name} theme`}
                                />
                            ))}
                        </div>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3">Token Icon</h3>
                        <div className="flex flex-wrap gap-2">
                            {TOKEN_ICONS.map(icon => (
                                <button
                                    key={icon}
                                    onClick={() => settings.setTokenIcon(icon)}
                                    className={`w-12 h-12 rounded-lg text-2xl flex items-center justify-center transition-all ${settings.tokenIcon === icon ? 'bg-[var(--primary-500)]/20 ring-2 ring-[var(--primary-500)]' : 'bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600'}`}
                                >
                                    {icon}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3">Background Image</h3>
                        <div className="flex gap-4">
                            <label className="flex-1 px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-white font-semibold rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors cursor-pointer text-center">
                                Upload Image
                                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                            </label>
                            <button onClick={handleClearImage} className="px-4 py-2 bg-slate-200 dark:bg-slate-600 text-slate-800 dark:text-white font-semibold rounded-lg hover:bg-slate-300 dark:hover:bg-slate-500 transition-colors">
                                Clear
                            </button>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <SizingButton label="Stretch" sizing="cover" />
                          <SizingButton label="Tile" sizing="repeat" />
                        </div>
                    </div>
                </div>
            )}
            {activeTab === 'quests' && (
                <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2">Reset Questlines</h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Resetting a questline lets you start it from the beginning. This is great for redoing big creative projects.</p>
                      <div className="max-h-60 overflow-y-auto space-y-2 pr-2 border-t border-slate-200 dark:border-slate-700 pt-4">
                          {questlines.length > 0 ? questlines.map(line => (
                              <div key={line.id} className="flex items-center justify-between bg-slate-100 dark:bg-slate-700 p-3 rounded-lg">
                                  <div>
                                      <p className="font-semibold text-slate-800 dark:text-slate-200">Reset: <span className="font-bold">{line.name}</span></p>
                                  </div>
                                  <button 
                                      onClick={() => quests.handleResetQuestline(line.id)}
                                      className="px-3 py-1 text-sm font-semibold bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                                  >
                                      Reset
                                  </button>
                              </div>
                          )) : (
                              <p className="text-center text-slate-500 dark:text-slate-400 py-4">No questlines found.</p>
                          )}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2">Rejected Quests</h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Here are quests you've previously rejected. You can restore them to make them available again.</p>
                      <div className="max-h-60 overflow-y-auto space-y-2 pr-2 border-t border-slate-200 dark:border-slate-700 pt-4">
                          {quests.rejectedQuests.length > 0 ? quests.rejectedQuests.map(quest => (
                              <div key={quest.id} className="flex items-center justify-between bg-slate-100 dark:bg-slate-700 p-3 rounded-lg">
                                  <div>
                                      <p className="font-semibold text-slate-800 dark:text-slate-200">{quest.title}</p>
                                      <p className="text-xs text-slate-500 dark:text-slate-400">{quest.category}</p>
                                  </div>
                                  <button 
                                      onClick={() => quests.handleRestoreQuest(quest)}
                                      className="px-3 py-1 text-sm font-semibold bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                                  >
                                      Restore
                                  </button>
                              </div>
                          )) : (
                              <p className="text-center text-slate-500 dark:text-slate-400 py-4">No rejected quests.</p>
                          )}
                      </div>
                    </div>
                </div>
            )}
            {activeTab === 'stats' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3">Lifetime Stats</h3>
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="bg-slate-100 dark:bg-slate-700/50 p-4 rounded-lg">
                        <p className="text-3xl font-bold" style={{color: 'var(--primary-500)'}}>{stats.lifetime.tokens}</p>
                        <p className="text-sm text-slate-600 dark:text-slate-400">Total Unspent Tokens</p>
                    </div>
                    <div className="bg-slate-100 dark:bg-slate-700/50 p-4 rounded-lg">
                        <p className="text-3xl font-bold" style={{color: 'var(--primary-500)'}}>{stats.lifetime.quests}</p>
                        <p className="text-sm text-slate-600 dark:text-slate-400">Total Quests Completed</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3">Stats By Year</h3>
                   <div className="space-y-4">
                      {stats.sortedYears.length > 0 ? stats.sortedYears.map(year => (
                        <div key={year} className="bg-slate-100 dark:bg-slate-700/50 p-4 rounded-lg">
                            <p className="font-bold text-slate-800 dark:text-slate-200 mb-2">{year}</p>
                            <div className="flex justify-around">
                                <p className="text-sm text-slate-600 dark:text-slate-400"><span className="font-bold text-lg text-slate-800 dark:text-slate-200">{stats.byYear[year].tokens}</span> Unspent Tokens</p>
                                <p className="text-sm text-slate-600 dark:text-slate-400"><span className="font-bold text-lg text-slate-800 dark:text-slate-200">{stats.byYear[year].quests}</span> Quests Completed</p>
                            </div>
                        </div>
                      )) : (
                        <p className="text-center text-slate-500 dark:text-slate-400 py-4">No yearly stats yet.</p>
                      )}
                   </div>
                </div>
              </div>
            )}
            {activeTab === 'data' && (
                <div className="space-y-8">
                    <div>
                        <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3">Manage Data</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Save your progress to a file to transfer it between devices.</p>
                        <div className="flex gap-4">
                             <button onClick={onExport} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors">
                                <DownloadIcon className="w-5 h-5"/>
                                Export Data
                            </button>
                             <label className="flex-1 px-4 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors cursor-pointer text-center">
                                Import Data
                                <input type="file" accept=".json" className="hidden" onChange={onImport} ref={importRef}/>
                            </label>
                        </div>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-red-500 dark:text-red-400 mb-3">Danger Zone</h3>
                        <div className="flex flex-col p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                            <p className="text-sm text-red-800 dark:text-red-200 mb-3">Permanently reset the application to its original state. All quests, tokens, and settings will be lost.</p>
                            <button onClick={handleResetClick} className="px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors self-start">
                                Reset App
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
